/**
 * Layout helper for the THCC product page.
 *
 * Horizon renders every block of the buy column inside `.product-details`. The Figma design needs some
 * of them next to the gallery, so this module re-parents them (nothing is cloned, so the per-variant
 * panels keep working with `assets/thcc-variant-panels.js`):
 *
 * - `[data-thcc-mover="media"]`: gallery extras. Moved once to `.product-information__media`, on top of
 *   the main photo.
 * - `[data-thcc-mover="bajo-galeria"]`: trust badges, club points and the assistant box. From 750 px up
 *   they live in a `.thcc-pdp-bajo-galeria` wrapper under the gallery; on mobile they go back to their
 *   original place in the buy column (a comment node marks it).
 *
 * Horizon only morphs the variant picker (not the details column) when a presentation changes, so the
 * moved nodes survive variant changes.
 */

const DESKTOP = window.matchMedia('(min-width: 750px)');

/** @type {WeakMap<Element, Comment>} */
const origins = new WeakMap();

/**
 * @param {Element} element
 * @returns {Element | null}
 */
function findMedia(element) {
  const root = element.closest('product-component') ?? document;
  return root.querySelector(':scope > .product-information__media') ?? document.querySelector('.product-information__media');
}

/**
 * @param {Element} media
 * @returns {HTMLElement}
 */
function ensureBelowGallery(media) {
  let wrapper = media.querySelector(':scope > .thcc-pdp-bajo-galeria');
  if (!(wrapper instanceof HTMLElement)) {
    wrapper = document.createElement('div');
    wrapper.className = 'thcc-pdp-bajo-galeria';
    media.append(wrapper);
  }
  return wrapper;
}

function apply() {
  for (const element of document.querySelectorAll('[data-thcc-mover]')) {
    if (!(element instanceof HTMLElement)) continue;
    const media = findMedia(element);
    if (!media) continue;

    if (element.dataset.thccMover === 'media') {
      if (element.parentElement !== media) media.append(element);
      continue;
    }

    if (element.dataset.thccMover !== 'bajo-galeria') continue;

    if (!origins.has(element) && element.parentNode) {
      const marker = document.createComment('thcc-origen');
      element.parentNode.insertBefore(marker, element);
      origins.set(element, marker);
    }

    if (DESKTOP.matches) {
      const wrapper = ensureBelowGallery(media);
      if (element.parentElement !== wrapper) wrapper.append(element);
    } else {
      const marker = origins.get(element);
      if (marker?.parentNode && element.previousSibling !== marker) {
        marker.parentNode.insertBefore(element, marker.nextSibling);
      }
    }
  }
}

DESKTOP.addEventListener('change', apply);
document.addEventListener('shopify:section:load', apply);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', apply, { once: true });
} else {
  apply();
}
